"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Menu as MenuIcon, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Drawer, DrawerContent, DrawerTrigger, DrawerClose, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="32066" className="flex flex-col items-center gap-6 animate-pulse">
        <div className="h-4 w-32 bg-muted rounded" />
        <div className="h-4 w-24 bg-muted rounded" />
        <div className="h-4 w-28 bg-muted rounded" />
        <div className="h-4 w-32 bg-muted rounded" />
      </div>;
  }
  return <nav className="flex flex-col items-center gap-8">
      {menuItems.map(item => <Link key={item.id} to={item.href || "#"} className="text-3xl font-serif italic text-foreground hover:text-primary transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const desktopTabClasses = "inline-flex items-center justify-center border border-primary px-5 py-2 text-[11px] font-sans uppercase tracking-[3px] text-foreground/80 hover:bg-primary hover:text-primary-foreground transition-all duration-300 whitespace-nowrap";
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-md border-primary/10 shadow-sm" : "bg-background")}>
      <div className="mx-auto max-w-[1280px] px-6 h-20">
        <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center h-full gap-6">
          {/* Left side */}
          <div className="flex justify-start pr-6 lg:pr-10">
            <nav className="flex items-center gap-3 lg:gap-4">
              <Link to="/HomePage" className={desktopTabClasses}>
                Home
              </Link>
              <Link to="/TheBigDayPage" className={desktopTabClasses}>
                Wedding Day Details
              </Link>
              <Link to="/TravelAndStayPage" className={desktopTabClasses}>
                Travel
              </Link>
            </nav>
          </div>

          {/* Center logo */}
          <div className="flex justify-center px-4 lg:px-6">
            <Link to="/HomePage" className="block">
              <img src="https://sara-michael-wedding.10web.cloud/wp-content/uploads/2026/04/download.png" alt="Michael&Sara" className="h-7 w-auto object-contain" />
            </Link>
          </div>

          {/* Right side */}
          <div className="flex justify-end pl-6 lg:pl-10">
            <nav className="flex items-center gap-3 lg:gap-4">
              <Link to="/RegistryPage" className={desktopTabClasses}>
                Registry
              </Link>
              <Link to="/RSVPPage" className={desktopTabClasses}>
                RSVP
              </Link>
              <Link to="/FAQPage" className={desktopTabClasses}>
                FAQ
              </Link>
            </nav>
          </div>
        </div>

        <div className="flex md:hidden items-center justify-between h-full">
          <Link to="/home" className="block relative z-50">
            <img src="https://sara-michael-wedding.10web.cloud/wp-content/uploads/2026/04/download.png" alt="Michael&Sara" className="h-7 w-auto object-contain" />
          </Link>

          <SafeHydrate><Drawer>
            <DrawerTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-transparent">
                <MenuIcon className="h-6 w-6 text-foreground" strokeWidth={1} />
                <span className="sr-only">Open menu</span>
              </Button>
            </DrawerTrigger>

            <DrawerContent className="h-[100dvh] mt-0 rounded-none flex flex-col bg-background">
              <div className="flex justify-end p-6">
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon" className="hover:bg-transparent">
                    <X className="h-6 w-6 text-foreground" strokeWidth={1} />
                    <span className="sr-only">Close menu</span>
                  </Button>
                </DrawerClose>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center p-6">
                <DrawerTitle className="sr-only">Navigation Menu</DrawerTitle>
                <DrawerDescription className="sr-only">
                  Main site navigation
                </DrawerDescription>

                <MenuProvider menu_id="2" className="w-full">
                  <MobileMenuConsumer />
                </MenuProvider>
              </div>
            </DrawerContent>
          </Drawer></SafeHydrate>
        </div>
      </div>
    </header>;
}